import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@sfc/ui";
import { Link } from "wouter";

/**
 * Sibling of `NotFoundCard`: the page-level surface shown when a route's
 * render throws. The reload is a full page reload on purpose — a render crash
 * usually means the in-memory round state is what's broken, and a fresh load
 * rebuilds it from the server. "Back to round" is a client-side navigation
 * on purpose: leaving the failed route clears the boundary (see
 * `RouteErrorBoundary`) while the live round shell stays up.
 *
 * The title copy is matched by the Bombadil nightly tripwire
 * (e2e/bombadil, ERROR_SURFACE_COPY) to detect round-route crashes; change
 * both together.
 */
export function RouteErrorCard({
  error,
  homeHref,
  homeLabel,
}: {
  error: Error;
  homeHref: string;
  homeLabel: string;
}) {
  return (
    <div className="flex flex-1 items-start justify-center p-6">
      <Card className="max-w-2xl" data-testid="route-error-card">
        <CardHeader>
          <CardTitle>This page ran into an error</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            We've been notified. Reloading usually fixes it; your saved work is
            not affected.
          </p>
          <code className="block rounded-md bg-muted px-3 py-2 text-sm break-all">
            {error.message}
          </code>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          <Button onClick={() => window.location.reload()}>Reload page</Button>
          <Button
            variant="outline"
            nativeButton={false}
            render={<Link href={homeHref} />}
          >
            {homeLabel}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
